import React, { Dispatch, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../redux/store';
import DynamicTree from '../../General/DynamicTree/DynamicTree';
import { DynamicTreeItemCategory } from '../../General/DynamicTree/DynamicTreeItemCategory';
import { categorizeImportDocuments } from '../../../util/ImportUtils';
import {
    getStructureItemInfo,
    getUnassignedCategory,
    IStructureItemInfo,
} from '../../../util/StructureUtils';
import { setExpandedNodes } from '../../../redux/import/sliceImportData';
import { setSelectedNodes } from '../../../redux/import/sliceDocnumberSidepanel';
import { toBeginUpperCase } from '../../../util/StringUtils';
import { useParams } from 'react-router-dom';
import { ToastSeverity } from '../../General/Toast/Toast';
import { useEnqueueSnackbar } from '../../../hooks/useEnqueueSnackbar';
import { useTranslation } from 'react-i18next';
import { StructureViewMode } from '../../../types';
import { getDocStructureFields } from '../../../util/DocUtils';
import { stringSort } from '../../../util/DataUtils';
import { Skeleton } from '@mui/material';

export interface ITreeItemInfo {
    name: string;
    fullPath: string;
    id: string;
    children: ITreeItemInfo[];
    docCount: number;
    isSkeleton?: boolean;
}

const getTreeItem = (item: IStructureItemInfo, all: IStructureItemInfo[]): ITreeItemInfo => {
    const childItems = all
        .filter(x => x.parent?.Subject === item.path)
        .sort((a, b) => stringSort(a.name, b.name))
        .map(x => getTreeItem(x, all));

    return {
        name: item.name,
        fullPath: item.path,
        id: item.id,
        children: childItems,
        docCount: item.docCount,
        isSkeleton: item.isSkeleton,
    };
};

const buildTree = (all: IStructureItemInfo[]): ITreeItemInfo[] => {
    const rootItems = all.filter(item => !item.parent).sort((a, b) => stringSort(a.name, b.name));

    return rootItems.map(item => getTreeItem(item, all));
};

const collectParents = (
    all: IStructureItemInfo[],
    results: IStructureItemInfo[],
    item: IStructureItemInfo,
) => {
    if (item.parent) {
        const parent = all.find(x => x.path === item.parent?.Subject);

        if (parent) {
            results.push(parent);
            collectParents(all, results, parent);
        }
    }
};

export default () => {
    const dispatch: Dispatch<unknown> = useDispatch();
    const enqueueSnackbar = useEnqueueSnackbar();

    const { t } = useTranslation('translations', { keyPrefix: 'importViewTree' });
    const { replicaId } = useParams();
    const { searchText, expandedNodes } = useSelector(
        (_store: IAppReduxStore) => _store.importData,
    );
    const { selectedNodes } = useSelector((_store: IAppReduxStore) => _store.docnumberSidepanel);
    const documents = useSelector((_store: IAppReduxStore) => _store.importDocuments.data);
    const viewName = useSelector((_store: IAppReduxStore) => _store.importData.currentView);
    const structures = useSelector(
        (_store: IAppReduxStore) => _store.importViewData.viewStructures,
    );
    const structure = structures.data[viewName];
    const selectedDocs = useSelector(
        (_store: IAppReduxStore) => _store.projectSelectedDocuments.storage.documentIds,
    );
    const [searchResults, setSearchResults] = useState<IStructureItemInfo[]>([]);

    const structureItems = useMemo(() => {
        const unassignedDocs = documents.filter(
            doc => getDocStructureFields(doc, viewName as StructureViewMode).length === 0,
        );
        const unassignedCtg = getUnassignedCategory(unassignedDocs.length);

        return (structure ?? [])
            .map(x => getStructureItemInfo(structure, x, true))
            .concat(unassignedDocs.length > 0 ? unassignedCtg : []);
    }, [structure, documents]);

    const onExpandedNodesChange = (_ev: React.SyntheticEvent, ids: string[]) => {
        dispatch(setExpandedNodes(ids));
    };

    const onNodeSelect = (_ev: React.SyntheticEvent, itemIds: string[]) => {
        const itemId = itemIds[0];
        const newSelectedNodes = itemId ? [itemId] : [];
        const node = structureItems.find(x => x.id === itemId);

        if (node) {
            const parentNodes = [];
            collectParents(structureItems, parentNodes, node);

            parentNodes.forEach(x => newSelectedNodes.push(x.id));
        }

        dispatch(setSelectedNodes({ current: node.id, all: newSelectedNodes }));
    };

    const onDropDocuments = (categoryId: string) => {
        const category = structureItems.find(x => x.id === categoryId);
        if (!category || category.isSkeleton) {
            return;
        }

        const categoryFields = {
            Id: category.id,
            FullPath: category.path,
        };

        categorizeImportDocuments(
            replicaId,
            selectedDocs,
            [categoryFields],
            'append',
            viewName as StructureViewMode,
        );
        enqueueSnackbar(t(`toastSuccess${toBeginUpperCase(viewName)}`), {
            severity: ToastSeverity.Success,
        });
    };

    const forceSelectionChange = (itemId: string, isSelected: boolean) => {
        const all = isSelected
            ? selectedNodes.filter(node => node !== itemId).concat([itemId])
            : selectedNodes.filter(node => node !== itemId);
        const current = isSelected ? itemId : all[0] ?? '';

        dispatch(setSelectedNodes({ current, all }));
    };

    const forceExpandedChange = (itemId: string, isExpanded: boolean) => {
        const current = expandedNodes === 'all' ? searchResults.map(x => x.id) : expandedNodes;
        const newState = current.filter(x => x !== itemId).concat(isExpanded ? [itemId] : []);
        dispatch(setExpandedNodes(newState));
    };

    useEffect(() => {
        if (!searchText) {
            dispatch(setExpandedNodes([]));
            setSearchResults(structureItems);
            return;
        }

        const filtered = structureItems.filter(x =>
            x.name.toLowerCase().includes(searchText.toLowerCase()),
        );

        const parents = [];
        filtered.forEach(x => collectParents(structureItems, parents, x));

        const results = [];
        [...filtered, ...parents].forEach(x => {
            if (!results.some(existing => existing.id === x.id)) {
                results.push(x);
            }
        });

        dispatch(setExpandedNodes(results.map(x => x.id)));
        setSearchResults(results);
    }, [structureItems, searchText]);

    const renderTreeItem = (item: ITreeItemInfo, paddingLeft: number) => {
        const itemProps = {
            id: item.id ?? item.fullPath,
            itemId: item.id ?? item.fullPath,
            key: item.id ?? item.fullPath,
            labelText: item.name,
            active: false,
            count: item.docCount,
            isSkeleton: item.isSkeleton,
        };

        const { key, ...otherProps } = itemProps;

        return (
            <DynamicTreeItemCategory
                {...otherProps}
                key={key}
                paddingLeft={paddingLeft}
                onDropDocuments={onDropDocuments}
                forceSelectionChangeFn={forceSelectionChange}
                forceExpandedChangeFn={forceExpandedChange}
            >
                {item.children.map(child => renderTreeItem({ ...child }, paddingLeft + 8))}
            </DynamicTreeItemCategory>
        );
    };

    if (structures.loading) {
        return (
            <>
                {[...Array(5).keys()].map(x => (
                    <Skeleton key={x} variant='text' animation='wave' height={40} />
                ))}
            </>
        );
    }

    if (!Object.keys(structures.data)?.length) {
        return <></>;
    }

    return (
        <DynamicTree
            selectedItems={selectedNodes}
            expandedItems={expandedNodes === 'all' ? searchResults.map(x => x.id) : expandedNodes}
            onExpandedItemsChange={onExpandedNodesChange}
            onSelectedItemsChange={onNodeSelect}
        >
            {buildTree(searchResults).map(item => renderTreeItem(item, 0))}
        </DynamicTree>
    );
};
